/** Segmento PN v2 — misma regla que solicitud-natural (PAR-068/069/071). */
export type PerfilSegmento = 'express' | 'tradicional';

export const PERFIL_SOLICITA_CREDITO_DRAFT_KEY = 'neocredito.solicitaCredito.draft';

/** Códigos PAR usados por el gate (alineados a solicitud.natural.form). */
export const PERFIL_PAR = {
  montoMaximoGlobal: 'PAR-057',
  montoMaxExpress: 'PAR-068',
  montoMinExpress: 'PAR-069',
  fechaRegistroMinimaMeses: 'PAR-070',
  plazoMaxExpress: 'PAR-071',
  textoMonto: 'PAR-072',
  textoFechaRegistro: 'PAR-073',
  plazoMinExpress: 'PAR-074',
  textoPlazo: 'PAR-075',
  seccionFlujoCaja: 'PAR-128',
  /** Meses mínimos desde fecha registro para exigir declaración de renta. */
  tiempoMinimoDeclaracionRenta: 'PAR-096',
  /** Umbral de ventas anuales para exigir declaración de renta. */
  valorLimiteVentas: 'PAR-117',
} as const;

export interface PerfilSegmentoParams {
  montoMaximoGlobal: number;
  montoMinExpress: number;
  montoMaxExpress: number;
  plazoMinExpress: number;
  plazoMaxExpress: number;
  /** PAR-128: fuerza flujo de caja también en Express. */
  seccionFlujoCaja: boolean;
  /** PAR-070: meses mínimos desde fecha cámara. */
  fechaRegistroMinimaMeses: number;
  /**
   * PAR-096. null = no cargado / no aplicar regla de antigüedad.
   */
  tiempoMinimoDeclaracionRenta: number | null;
  /**
   * PAR-117. null = no cargado / no aplicar regla de ventas.
   */
  valorLimiteVentas: number | null;
  mensajeMonto?: string;
  mensajePlazo?: string;
  mensajeFechaRegistro?: string;
}

/** Fallback QA si el API de parámetros falla. */
export const PERFIL_SEGMENTO_PARAMS_FALLBACK: PerfilSegmentoParams = {
  montoMaximoGlobal: 50_000_000,
  montoMinExpress: 1,
  montoMaxExpress: 10_000_000,
  plazoMinExpress: 1,
  plazoMaxExpress: 36,
  seccionFlujoCaja: false,
  fechaRegistroMinimaMeses: 0,
  tiempoMinimoDeclaracionRenta: null,
  valorLimiteVentas: null,
};

export interface PerfilSolicitaCreditoDraft {
  monto?: string;
  plazo?: string | number;
  tipoCredito?: unknown;
  destinos?: string[];
  /** Ids numéricos NEO_DESTINO_CREDITO marcados. */
  idsDestinoCredito?: number[];
  /** BANCOLDEX | ALIADOS | AMBAS */
  tipoLinea?: 'BANCOLDEX' | 'ALIADOS' | 'AMBAS' | null;
  otroTexto?: string | null;
  segmento?: PerfilSegmento;
  muestraFlujoCaja?: boolean;
  /** Id de solicitud creada al continuar desde solicita-crédito. */
  idSolicitud?: string;
}

/** Mapea checkboxes UI → TIPO_LINEA SQL. */
export function resolveTipoLinea(
  tipoCredito: { bancoldex?: boolean; aliados?: boolean; ambas?: boolean } | null | undefined
): 'BANCOLDEX' | 'ALIADOS' | 'AMBAS' | null {
  if (!tipoCredito) {
    return null;
  }
  if (tipoCredito.ambas || (tipoCredito.bancoldex && tipoCredito.aliados)) {
    return 'AMBAS';
  }
  if (tipoCredito.bancoldex) {
    return 'BANCOLDEX';
  }
  if (tipoCredito.aliados) {
    return 'ALIADOS';
  }
  return null;
}

export function parseMontoDigits(value: unknown): number {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) {
    return 0;
  }
  return Number(digits);
}

export function parsePlazoNumber(value: unknown): number {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) {
    return 0;
  }
  return Number(digits);
}

/**
 * Express si monto ∈ [PAR-069, PAR-068] y plazo ≤ PAR-071.
 * Todo lo demás → tradicional.
 */
export function resolveIsExpress(
  monto: number,
  plazo: number,
  params: Pick<
    PerfilSegmentoParams,
    'montoMinExpress' | 'montoMaxExpress' | 'plazoMaxExpress'
  >
): boolean {
  if (!monto || !plazo) {
    return false;
  }
  return (
    monto >= params.montoMinExpress &&
    monto <= params.montoMaxExpress &&
    plazo <= params.plazoMaxExpress
  );
}

export function resolveSegmento(
  monto: number,
  plazo: number,
  params: Pick<
    PerfilSegmentoParams,
    'montoMinExpress' | 'montoMaxExpress' | 'plazoMaxExpress'
  >
): PerfilSegmento {
  return resolveIsExpress(monto, plazo, params) ? 'express' : 'tradicional';
}

/** Visible/obligatorio: Tradicional o Express con PAR-128. */
export function resolveMuestraFlujoCaja(
  segmento: PerfilSegmento,
  seccionFlujoCaja: boolean
): boolean {
  return segmento === 'tradicional' || seccionFlujoCaja;
}

/**
 * Misma regla que validarFechaRegistro:
 * fecha + meses(PAR-070) no puede quedar en el futuro.
 */
export function isFechaRegistroCamaraInvalida(
  fechaIso: string | null | undefined,
  mesesMinimos: number,
  hoy: Date = new Date()
): boolean {
  if (!fechaIso) {
    return false;
  }
  const base = new Date(fechaIso);
  if (Number.isNaN(base.getTime())) {
    return true;
  }
  const minima = new Date(base);
  minima.setMonth(minima.getMonth() + (mesesMinimos || 0));
  const hoyIso = hoy.toLocaleDateString('en-CA');
  const minimaIso = minima.toLocaleDateString('en-CA');
  return minimaIso > hoyIso;
}

export function readSolicitaCreditoDraft(): PerfilSolicitaCreditoDraft | null {
  try {
    const raw = sessionStorage.getItem(PERFIL_SOLICITA_CREDITO_DRAFT_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as PerfilSolicitaCreditoDraft;
  } catch {
    return null;
  }
}

export function writeSolicitaCreditoDraft(
  draft: PerfilSolicitaCreditoDraft
): void {
  try {
    sessionStorage.setItem(
      PERFIL_SOLICITA_CREDITO_DRAFT_KEY,
      JSON.stringify(draft)
    );
  } catch {
    /* ignore quota */
  }
}
